import {ref, markRaw, nextTick} from 'vue';
import {createApp, h} from 'vue';
import AddressRequiredModal from '@/frontend/vue/components/Modal/AddressRequiredModal.vue';
import { useDeliveryConfig } from './useDeliveryConfig';

// Get delivery config functionality
const { getDeliveryConfig } = useDeliveryConfig();

// Create a container for the modal if it doesn't exist
function ensureModalContainer() {
    let container = document.getElementById('address-modal-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'address-modal-container';
        document.body.appendChild(container);
    }
    return container;
}


// State to track if modal is already mounted
const isModalMounted = ref(false);
const modalInstance = ref(null);
const pendingProducts = ref(null);

// Function to show the modal
export function showAddressRequiredModal(products) {
    // Store the products for later use
    pendingProducts.value = products;

    // First check if delivery is enabled from config
    getDeliveryConfig().then(config => {
        // If delivery is disabled and pickup is enabled, we don't need to show the address modal
        // Just trigger the continue event directly
        if (!config.deliveryEnabled && config.pickupEnabled) {
            // Store pickup data
            const clickCollectData = {
                dispatch: 'pickup',
                address: '',
                suburb: '',
                state: '',
                postcode: ''
            };
            sessionStorage.setItem('checkoutAddressData', JSON.stringify(clickCollectData));

            // Trigger events to continue
            handleContinue();
            return;
        }

        // Otherwise, show the modal as usual
        if (!isModalMounted.value) {
            mountModal();
        } else if (modalInstance.value) {
            modalInstance.value.show();
        } else {
            // Try remounting the modal if the instance doesn't exist
            isModalMounted.value = false;
            mountModal();
        }
    });
}

// Function to mount the modal
async function mountModal() {
    const container = ensureModalContainer();

    // Get business info from the page
    // Try to get from the product page elements first
    let businessName = 'Store';
    let businessAddress = '';

    // Find the business name in the click and collect section
    const businessNameElement = document.querySelector('#clickcollect-section .message-box__label h4');
    if (businessNameElement) {
        businessName = businessNameElement.textContent.trim();
    }

    // Find the business address in the click and collect section
    const businessAddressElement = document.querySelector('#clickcollect-section .message-box__label');
    if (businessAddressElement) {
        // Get all text nodes that are not inside h4
        const addressNodes = Array.from(businessAddressElement.childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE ||
                (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() !== 'h4'));

        // Combine the text content
        businessAddress = addressNodes.map(node => node.textContent || node.innerText).join('\n').trim();
    }

    // Get Google Maps API key from the delivery-address-lookup component or from a global variable
    let googleMapsApiKey = '';

    // Try to get from delivery-address-lookup component
    const addressLookupElement = document.querySelector('delivery-address-lookup');
    if (addressLookupElement && addressLookupElement.getAttribute('google-maps-api-key')) {
        googleMapsApiKey = addressLookupElement.getAttribute('google-maps-api-key');
    }
    // Fallback to window variable if available
    else if (window.GOOGLE_MAPS_API_KEY) {
        googleMapsApiKey = window.GOOGLE_MAPS_API_KEY;
    }

    // Get delivery and pickup configuration from API or session storage
    const config = await getDeliveryConfig();

    // Create a new Vue app for the modal
    const app = createApp({
        render() {
            // Extract productHandle from pendingProducts if available
            let productHandle = '';
            if (pendingProducts.value) {
                if (Array.isArray(pendingProducts.value) && pendingProducts.value.length > 0) {
                    // If it's an array, get the handle from the first product
                    productHandle = pendingProducts.value[0].handle || '';
                } else if (pendingProducts.value.handle) {
                    // If it's a single product object
                    productHandle = pendingProducts.value.handle;
                }
            }

            return h(AddressRequiredModal, {
                ref: 'modal',
                googleMapsApiKey,
                businessName,
                businessAddress,
                productHandle,
                deliveryLabel: config.deliveryLabel,
                pickupLabel: config.pickupLabel,
                deliveryHeading: config.deliveryHeading,
                pickupHeading: config.pickupHeading,
                deliveryEnabled: config.deliveryEnabled,
                pickupEnabled: config.pickupEnabled,
                onContinue: handleContinue
            });
        }
    });

    // Mount the app
    const mountedApp = app.mount(container);

    // Store the modal instance
    modalInstance.value = mountedApp.$refs.modal;
    isModalMounted.value = true;

    // Show the modal
    await nextTick();
    if (modalInstance.value) {
        modalInstance.value.show();
    }
}

function handleContinue() {
    window.dispatchEvent(new CustomEvent('address-updated'));
    window.dispatchEvent(new CustomEvent('apply-cart'));
}

export function hasDeliveryData() {
    if (typeof window === 'undefined') return false;

    try {
        // First check if there's stored delivery data
        const storedData = sessionStorage.getItem('ec_checkout_addr');
        if (!!storedData) {
            return true;
        }

        // If no stored data, check if delivery is even enabled
        // If it's not enabled, we can consider this as having delivery data
        // since we don't need to collect it
        const { deliveryConfig } = useDeliveryConfig();
        if (deliveryConfig.value) {
            if (!deliveryConfig.value.deliveryEnabled && deliveryConfig.value.pickupEnabled) {
                // If delivery is disabled but pickup is enabled,
                // we don't need delivery data
                return true;
            }
        } else {
            // If deliveryConfig is not loaded yet, check session storage
            const storedConfig = sessionStorage.getItem('ec_delivery_config');
            if (storedConfig) {
                const { data } = JSON.parse(storedConfig);
                if (!data.deliveryEnabled && data.pickupEnabled) {
                    // If delivery is disabled but pickup is enabled,
                    // we don't need delivery data
                    return true;
                }
            }
        }

        return false;
    } catch (e) {
        console.error('Error checking delivery data:', e);
        return false;
    }
}

export function isPickup() {
    if (typeof window === 'undefined') return false;

    const modalPickup = document.getElementById('modal-pickup');
    const addressRequiredModal = document.getElementById('addressRequiredModal');
    const pickup = document.getElementById('pickup');

    const isModalVisible = addressRequiredModal?.offsetParent !== null;

    if (modalPickup && isModalVisible) {
        return modalPickup.checked === true;
    }

    return pickup?.checked === true;
}

export function useAddressModal() {
    return {
        showAddressRequiredModal,
        hasDeliveryData,
        isPickup
    };
}
